import React from 'react';
import classnames from 'classnames/bind';
import { ContentChapter, ContentSection } from '@INTERFACES/api/content';
import BasicCheckbox from '@COMPONENTS/COMMON/inputs/checkbox/BasicCheckbox';
import styles from './CompactContentTreeView.module.scss';

const cx: CX = classnames.bind(styles);

interface CompactContentTreeViewProps {
    chaptersData: ContentChapter[];
    selectedChaptersIds: Set<number>;
    selectedSectionsIds: Set<number>;
    selectedVariantsIds: Set<number>;
    setSelectedChaptersIds: (chapters: Set<number>) => void;
    setSelectedSectionsIds: (sections: Set<number>) => void;
    setSelectedVariantsIds: (variants: Set<number>) => void;
    isPartial: boolean;
}

function CompactContentTreeView({
    chaptersData,
    selectedChaptersIds,
    selectedSectionsIds,
    selectedVariantsIds,
    setSelectedChaptersIds,
    setSelectedSectionsIds,
    setSelectedVariantsIds,
    isPartial,
}: CompactContentTreeViewProps) {
    const handleChapterChange = (chapterId: number, checked: boolean) => {
        const newSelectedChapters = new Set(selectedChaptersIds);
        const newSelectedSections = new Set(selectedSectionsIds);

        if (checked) {
            newSelectedChapters.add(chapterId);

            // Auto-select all sections and sub-sections in this chapter
            const chapter = chaptersData.find(c => c.id === chapterId);
            if (chapter) {
                const selectSectionAndChildren = (section: any) => {
                    newSelectedSections.add(section.id);
                    // Select children (sub-sections) recursively
                    section.children?.forEach((child: any) => {
                        selectSectionAndChildren(child);
                    });
                };

                chapter.sections.forEach(selectSectionAndChildren);
            }
        } else {
            newSelectedChapters.delete(chapterId);

            // Auto-deselect all sections and sub-sections in this chapter
            const chapter = chaptersData.find(c => c.id === chapterId);
            if (chapter) {
                const deselectSectionAndChildren = (section: any) => {
                    newSelectedSections.delete(section.id);
                    // Deselect children (sub-sections) recursively
                    section.children?.forEach((child: any) => {
                        deselectSectionAndChildren(child);
                    });
                };

                chapter.sections.forEach(deselectSectionAndChildren);
            }
        }

        setSelectedChaptersIds(newSelectedChapters);
        setSelectedSectionsIds(newSelectedSections);
    };

    const handleSectionChange = (sectionId: number, chapterId: number, checked: boolean) => {
        const newSelectedSections = new Set(selectedSectionsIds);

        const chapter = chaptersData.find(c => c.id === chapterId);
        // Look for section in main sections or in any nested children
        const findSection = (sections: any[]): any => {
            // First check if any section at this level matches
            const directMatch = sections.find(sect => sect.id === sectionId);
            if (directMatch) return directMatch;

            // Then recursively search in children of each section
            const nestedMatch = sections
                .filter(sect => sect.children && sect.children.length > 0)
                .map(sect => findSection(sect.children))
                .find(result => result !== null);

            return nestedMatch || null;
        };

        const section = chapter ? findSection(chapter.sections) : null;

        if (checked) {
            newSelectedSections.add(sectionId);
            // Auto-select all children in this section
            const selectSectionContent = (sect: any) => {
                sect.children?.forEach((child: any) => {
                    newSelectedSections.add(child.id);
                    selectSectionContent(child);
                });
            };
            if (section) selectSectionContent(section);
        } else {
            newSelectedSections.delete(sectionId);
            // Auto-deselect all children in this section
            const deselectSectionContent = (sect: any) => {
                sect.children?.forEach((child: any) => {
                    newSelectedSections.delete(child.id);
                    deselectSectionContent(child);
                });
            };
            if (section) deselectSectionContent(section);
        }

        setSelectedSectionsIds(newSelectedSections);
    };

    // Removed variant handling - variants are no longer displayed

    const isChapterFullySelected = (chapter: ContentChapter): boolean => {
        const checkAllSectionsSelected = (sections: any[]): boolean => sections.every((section) => {
            const sectionSelected = selectedSectionsIds.has(section.id);
            const childrenAllSelected = section.children ? checkAllSectionsSelected(section.children) : true;
            // Section is fully selected if either it's explicitly selected OR all its children are selected
            return sectionSelected || (section.children && section.children.length > 0 && childrenAllSelected);
        });

        return chapter.sections.length > 0 && checkAllSectionsSelected(chapter.sections);
    };

    const isChapterPartiallySelected = (chapter: ContentChapter): boolean => {
        const chapterSelected = selectedChaptersIds.has(chapter.id);
        const fullySelected = isChapterFullySelected(chapter);

        const checkSectionTree = (section: any): boolean => {
            const sectionSelected = selectedSectionsIds.has(section.id);
            const childrenSelected = section.children?.some((child: any) => checkSectionTree(child));

            return sectionSelected || childrenSelected;
        };

        const somethingSelected = chapter.sections.some(checkSectionTree);

        return !chapterSelected && !fullySelected && somethingSelected;
    };

    const isSectionFullySelected = (section: ContentSection): boolean => {
        const checkAllChildrenSelected = (children: any[]): boolean => children.every((child) => {
            const childSelected = selectedSectionsIds.has(child.id);
            const grandchildrenAllSelected = child.children ? checkAllChildrenSelected(child.children) : true;
            // Child is fully selected if either it's explicitly selected OR all its children are selected
            return childSelected || (child.children && child.children.length > 0 && grandchildrenAllSelected);
        });

        const sectionSelected = selectedSectionsIds.has(section.id);
        const allChildrenSelected = (section as any).children ? checkAllChildrenSelected((section as any).children) : true;

        // Section is fully selected if either it's explicitly selected OR all its children are selected
        return sectionSelected || ((section as any).children && (section as any).children.length > 0 && allChildrenSelected);
    };

    const checkSectionPartiallySelected = (section: ContentSection): boolean => {
        const sectionSelected = selectedSectionsIds.has(section.id);
        const fullySelected = isSectionFullySelected(section);

        const someChildrenSelected = (section as any).children?.some((child: any) => {
            const childSelected = selectedSectionsIds.has(child.id);
            const childChildrenSelected = child.children?.some((grandchild: any) => selectedSectionsIds.has(grandchild.id));
            return childSelected || childChildrenSelected;
        });

        return !sectionSelected && !fullySelected && Boolean(someChildrenSelected);
    };

    return (
        <div className={cx('compact-tree-view')}>
            {chaptersData.filter(chapter => chapter.name && chapter.name.trim()).map((chapter) => {
                const isChapterSelected = selectedChaptersIds.has(chapter.id) || isChapterFullySelected(chapter);
                const isPartiallySelected = isChapterPartiallySelected(chapter);

                return (
                    <div key={chapter.id} className={cx('chapter-item')}>
                        <div className={cx('chapter-header')}>
                            <BasicCheckbox
                                checked={isChapterSelected}
                                indeterminate={!isChapterSelected && isPartiallySelected}
                                onChange={(event) => handleChapterChange(chapter.id, event.target.checked)}
                                title={chapter.name}
                                fontSize={16}
                                fontWeight={700}
                            />

                        </div>

                        {chapter.sections.filter(section => section.name && section.name.trim()).length > 0 && (
                            <div className={cx('sections-list')}>
                                {chapter.sections.filter(section => section.name && section.name.trim()).map((section) => {
                                    const isSectionSelected = selectedSectionsIds.has(section.id) || isSectionFullySelected(section);
                                    const isSectionPartiallySelected = checkSectionPartiallySelected(section);

                                    return (
                                        <div key={section.id} className={cx('section-item')}>
                                            <div className={cx('section-content')}>
                                                <div className={cx('section-header')}>
                                                    <BasicCheckbox
                                                        checked={isSectionSelected}
                                                        indeterminate={!isSectionSelected && isSectionPartiallySelected}
                                                        onChange={(event) => handleSectionChange(section.id, chapter.id, event.target.checked)}
                                                        title={section.name}
                                                        fontSize={12}
                                                        fontWeight={500}
                                                    />
                                                </div>

                                                {/* Render sub-sections (children) */}
                                                {section.children && section.children.filter(child => child.name && child.name.trim()).length > 0 && (
                                                    <div className={cx('subsections-list')}>
                                                        {section.children.filter(child => child.name && child.name.trim()).map((subsection) => {
                                                            const isSubsectionSelected = selectedSectionsIds.has(subsection.id) || isSectionFullySelected(subsection);
                                                            const isSubsectionPartiallySelected = checkSectionPartiallySelected(subsection);

                                                            return (
                                                                <div key={subsection.id} className={cx('subsection-item')}>
                                                                    <div className={cx('subsection-content')}>
                                                                        <div className={cx('subsection-header')}>
                                                                            <BasicCheckbox
                                                                                checked={isSubsectionSelected}
                                                                                indeterminate={!isSubsectionSelected && isSubsectionPartiallySelected}
                                                                                onChange={(event) => handleSectionChange(subsection.id, chapter.id, event.target.checked)}
                                                                                title={subsection.name}
                                                                                fontSize={11}
                                                                                fontWeight={400}
                                                                            />
                                                                        </div>

                                                                        {/* Render sub-sub-sections if they exist */}
                                                                        {subsection.children && subsection.children.filter(child => child.name && child.name.trim()).length > 0 && (
                                                                            <div className={cx('subsubsections-list')}>
                                                                                {subsection.children.filter(child => child.name && child.name.trim()).map((subsubsection) => {
                                                                                    const isSubsubsectionSelected = selectedSectionsIds.has(subsubsection.id);

                                                                                    return (
                                                                                        <div key={subsubsection.id} className={cx('subsubsection-item')}>
                                                                                            <div className={cx('subsubsection-content')}>
                                                                                                <div className={cx('subsubsection-header')}>
                                                                                                    <BasicCheckbox
                                                                                                        checked={isSubsubsectionSelected}
                                                                                                        onChange={(event) => handleSectionChange(subsubsection.id, chapter.id, event.target.checked)}
                                                                                                        title={subsubsection.name}
                                                                                                        fontSize={10}
                                                                                                        fontWeight={400}
                                                                                                    />
                                                                                                </div>
                                                                                            </div>
                                                                                        </div>
                                                                                    );
                                                                                })}
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                );
            })}
        </div>
    );
}

export default CompactContentTreeView; 